import { rgbAt } from '../image/analysis.js';
import type { NormalizedRect } from '../types/geometry.js';
import { clamp, clamp01, roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';

/**
 * Face-region pixel analysis behind the content checks.
 *
 * This module owns the two things every pixel-based face check needs:
 *
 *  1. *Where* the face regions are (eyes, nose, mouth, cheeks) — derived from
 *     landmarks, never from fixed fractions of the frame.
 *  2. *How* a single pixel is classified relative to a measured skin reference
 *     (hair-like = dark, low-saturation and low-detail; skin-like = chromatic
 *     and not too saturated; everything else is "foreign").
 *
 * The classification is deliberately exposed as a small strategy object with a
 * `mode` field: the shipped implementation is `'heuristic'`, and a future
 * segmentation mask can be dropped in as `'segmentation'` without touching the
 * checks. Nothing here reads the whole image: every scan is bounded by a ROI.
 */

export interface PixelFeatures {
  r: number;
  g: number;
  b: number;
  /** ITU-R BT.601 luma, 0..255. */
  luma: number;
  /** (max - min) / max, 0 = achromatic, 1 = fully saturated. */
  saturation: number;
  /** max - min, 0..255. */
  chroma: number;
}

export const pixelFeatures = (r: number, g: number, b: number): PixelFeatures => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return {
    r,
    g,
    b,
    luma: 0.299 * r + 0.587 * g + 0.114 * b,
    saturation: max > 0 ? (max - min) / max : 0,
    chroma: max - min,
  };
};

/** Mean absolute luma difference to the 4 direct neighbours. */
export const localDetailAt = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): number => {
  const center = luma[y * width + x] as number;
  let sum = 0;
  let count = 0;
  const neighbours = [
    [x - 1, y],
    [x + 1, y],
    [x, y - 1],
    [x, y + 1],
  ] as const;
  for (const [nx, ny] of neighbours) {
    if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
    sum += Math.abs((luma[ny * width + nx] as number) - center);
    count += 1;
  }
  return count > 0 ? sum / count : 0;
};

export interface PixelBounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Pixel bounds of a normalized rect, clamped to the buffer. */
export const pixelBounds = (
  rect: NormalizedRect,
  width: number,
  height: number,
): PixelBounds => {
  const x0 = clamp(Math.floor(rect.x * width), 0, width);
  const y0 = clamp(Math.floor(rect.y * height), 0, height);
  const x1 = clamp(Math.ceil((rect.x + rect.width) * width), x0, width);
  const y1 = clamp(Math.ceil((rect.y + rect.height) * height), y0, height);
  return { x0, y0, x1, y1 };
};

/** Rect shrunk by `fraction` of its own width/height on every side. */
export const insetRect = (rect: NormalizedRect, fraction: number): NormalizedRect => ({
  x: rect.x + rect.width * fraction,
  y: rect.y + rect.height * fraction,
  width: rect.width * (1 - 2 * fraction),
  height: rect.height * (1 - 2 * fraction),
});

export const clampRect = (rect: NormalizedRect): NormalizedRect => {
  const x = clamp(rect.x, 0, 1);
  const y = clamp(rect.y, 0, 1);
  return {
    x,
    y,
    width: clamp(rect.width, 0, 1 - x),
    height: clamp(rect.height, 0, 1 - y),
  };
};

/** Colour/texture statistics of a reference patch known to be skin. */
export interface SkinReference {
  luma: number;
  saturation: number;
  detail: number;
  sampleCount: number;
  rect: NormalizedRect;
}

/**
 * Measures a skin reference from a patch.
 *
 * The luma reference is the 70th percentile rather than the mean: hair, shadow
 * and background corners only pull a percentile down, never up, so the
 * threshold derived from it stays on the safe side.
 */
export const computeSkinReference = (
  pixels: AnalysisBuffer,
  rect: NormalizedRect,
): SkinReference => {
  const { data, width, height, channels, luma } = pixels;
  const { x0, y0, x1, y1 } = pixelBounds(rect, width, height);
  const lumas: number[] = [];
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      lumas.push(luma[y * width + x] as number);
    }
  }
  if (lumas.length === 0) {
    return { luma: 128, saturation: 0.25, detail: 4, sampleCount: 0, rect: clampRect(rect) };
  }
  const sorted = [...lumas].sort((a, b) => a - b);
  const quantile = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.7))] as number;

  const saturations: number[] = [];
  let detailSum = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const colour = rgbAt(data, width, channels, x, y);
      const features = pixelFeatures(colour.r, colour.g, colour.b);
      detailSum += localDetailAt(luma, width, height, x, y);
      if (features.luma >= quantile * 0.85) saturations.push(features.saturation);
    }
  }
  const sortedSaturation = saturations.sort((a, b) => a - b);
  const saturation =
    sortedSaturation.length > 0
      ? (sortedSaturation[Math.floor(sortedSaturation.length / 2)] as number)
      : 0.25;

  return {
    luma: roundTo(quantile, 1),
    saturation: roundTo(saturation, 3),
    detail: roundTo(detailCount(detailSum, x0, y0, x1, y1), 3),
    sampleCount: lumas.length,
    rect: clampRect(rect),
  };
};

const detailCount = (sum: number, x0: number, y0: number, x1: number, y1: number): number => {
  const count = Math.max(1, (x1 - x0) * (y1 - y0));
  return sum / count;
};

export interface HairHeuristicOptions {
  skinLuma: number;
  skinSaturation: number;
  skinDetail: number;
  /** Hair luma ceiling as a share of the skin luma. */
  darkFactor: number;
  /** Hair saturation ceiling as a multiple of the skin saturation. */
  saturationFactor: number;
  /** Hair detail ceiling as a multiple of the skin detail. */
  detailFactor: number;
}

export const heuristicOptions = (
  reference: SkinReference,
  overrides: Partial<Omit<HairHeuristicOptions, 'skinLuma' | 'skinSaturation' | 'skinDetail'>> = {},
): HairHeuristicOptions => ({
  skinLuma: reference.luma,
  skinSaturation: reference.saturation,
  skinDetail: reference.detail,
  darkFactor: overrides.darkFactor ?? 0.55,
  saturationFactor: overrides.saturationFactor ?? 1.25,
  detailFactor: overrides.detailFactor ?? 1.5,
});

/** Dark, low-saturation and locally flat — the classic hair signature. */
export const isHairLike = (
  features: PixelFeatures,
  detail: number,
  options: HairHeuristicOptions,
): boolean =>
  features.luma <= options.skinLuma * options.darkFactor &&
  features.saturation <= Math.max(options.skinSaturation * options.saturationFactor, 0.32) &&
  detail <= options.skinDetail * options.detailFactor + 4;

/** Chromatic, moderate-saturation pixel — a skin candidate. */
export const isSkinLike = (features: PixelFeatures, options: HairHeuristicOptions): boolean =>
  features.luma >= options.skinLuma * 0.4 &&
  features.chroma >= 4 &&
  features.r >= features.b &&
  features.saturation <= clamp(options.skinSaturation * 2.2 + 0.12, 0.35, 0.75);

export type FaceRegionId = 'left_eye' | 'right_eye' | 'nose' | 'mouth' | 'cheeks';

export interface FaceRegion {
  id: FaceRegionId;
  rect: NormalizedRect;
}

/**
 * The five regions every content check reasons about, anchored to landmarks.
 * Eye boxes follow the measured eye separation, so a small or turned face moves
 * them with it instead of leaving them at a fixed place in the frame.
 */
export const faceRegionRects = (geometry: FaceGeometryMetrics): FaceRegion[] => {
  const faceWidth = geometry.faceBox.width > 0 ? geometry.faceBox.width : geometry.headWidth;
  const faceHeight = geometry.faceHeight > 0 ? geometry.faceHeight : geometry.faceBox.height;
  const eyeWidth = Math.max(geometry.eyeDistance * 0.42, faceWidth * 0.18);
  const eyeHeight = eyeWidth * 0.8;
  const around = (
    id: FaceRegionId,
    center: { x: number; y: number },
    width: number,
    height: number,
  ): FaceRegion => ({
    id,
    rect: { x: center.x - width / 2, y: center.y - height / 2, width, height },
  });
  return [
    around('left_eye', geometry.eyeLeft, eyeWidth, eyeHeight),
    around('right_eye', geometry.eyeRight, eyeWidth, eyeHeight),
    around('nose', geometry.noseTip, faceWidth * 0.24, faceHeight * 0.18),
    around('mouth', geometry.mouthCenter, faceWidth * 0.4, faceHeight * 0.16),
    around(
      'cheeks',
      { x: geometry.faceCenterX, y: geometry.noseTip.y + faceHeight * 0.08 },
      faceWidth * 0.7,
      faceHeight * 0.14,
    ),
  ];
};

export interface RegionCoverage {
  id: string;
  rect: NormalizedRect;
  pixels: number;
  hairPixels: number;
  skinPixels: number;
  foreignPixels: number;
  /** Share of ROI pixels classified as hair-like, 0..1. */
  coverage: number;
  /** Mean local detail of the hair-like pixels (0 = perfectly smooth). */
  hairDetail: number;
  /** Mean local detail of the foreign pixels (0 = perfectly smooth). */
  foreignDetail: number;
}

/** Classifies every pixel of one ROI against the skin reference. */
export const measureRegion = (
  pixels: AnalysisBuffer,
  id: string,
  region: NormalizedRect,
  options: HairHeuristicOptions,
): RegionCoverage => {
  const { data, width, height, channels, luma } = pixels;
  const rect = clampRect(region);
  const { x0, y0, x1, y1 } = pixelBounds(rect, width, height);
  let total = 0;
  let hairPixels = 0;
  let skinPixels = 0;
  let foreignPixels = 0;
  let hairDetailSum = 0;
  let foreignDetailSum = 0;

  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const colour = rgbAt(data, width, channels, x, y);
      const features = pixelFeatures(colour.r, colour.g, colour.b);
      const detail = localDetailAt(luma, width, height, x, y);
      total += 1;
      if (isHairLike(features, detail, options)) {
        hairPixels += 1;
        hairDetailSum += detail;
      } else if (isSkinLike(features, options)) {
        skinPixels += 1;
      } else {
        foreignPixels += 1;
        foreignDetailSum += detail;
      }
    }
  }

  return {
    id,
    rect,
    pixels: total,
    hairPixels,
    skinPixels,
    foreignPixels,
    coverage: total > 0 ? hairPixels / total : 0,
    hairDetail: hairPixels > 0 ? hairDetailSum / hairPixels : 0,
    foreignDetail: foreignPixels > 0 ? foreignDetailSum / foreignPixels : 0,
  };
};

export interface HairRegionResult extends RegionCoverage {
  /** How much this region's verdict can be trusted, 0..1. */
  confidence: number;
}

export interface HairOcclusionAnalysis {
  regions: HairRegionResult[];
  /** Region with the largest hair coverage. */
  worst: HairRegionResult | undefined;
  maxCoverage: number;
  confidence: number;
  skinReference: SkinReference;
  notes: string[];
}

/** Small strategy object: swap in a segmentation mask by changing `mode`. */
export interface HairOcclusionAnalyzer {
  mode: 'heuristic' | 'segmentation';
  analyse(
    pixels: AnalysisBuffer,
    geometry: FaceGeometryMetrics,
    reference: SkinReference,
    overrides?: Partial<
      Omit<HairHeuristicOptions, 'skinLuma' | 'skinSaturation' | 'skinDetail'>
    >,
  ): HairOcclusionAnalysis;
}

const MIN_REGION_PIXELS = 40;

const regionConfidence = (region: RegionCoverage): number =>
  clamp01(0.35 + 0.55 * clamp01(region.pixels / (MIN_REGION_PIXELS * 4)));

const analyseHeuristically = (
  pixels: AnalysisBuffer,
  geometry: FaceGeometryMetrics,
  reference: SkinReference,
  overrides: Partial<
    Omit<HairHeuristicOptions, 'skinLuma' | 'skinSaturation' | 'skinDetail'>
  > = {},
): HairOcclusionAnalysis => {
  const options = heuristicOptions(reference, overrides);
  const regions: HairRegionResult[] = faceRegionRects(geometry).map((region) => {
    const coverage = measureRegion(pixels, region.id, region.rect, options);
    return { ...coverage, confidence: regionConfidence(coverage) };
  });

  let worst: HairRegionResult | undefined;
  for (const region of regions) {
    if (!worst || region.coverage > worst.coverage) worst = region;
  }

  const eyes = regions.filter((region) => region.id === 'left_eye' || region.id === 'right_eye');
  const sizeConfidence =
    eyes.length > 0
      ? eyes.reduce((sum, region) => sum + region.confidence, 0) / eyes.length
      : 0;
  const notes: string[] = [];
  if (sizeConfidence < 0.6) {
    notes.push('Область глаз мала в анализируемом буфере: покрытие измерено грубо.');
  }

  return {
    regions,
    worst,
    maxCoverage: worst?.coverage ?? 0,
    confidence: clamp01(sizeConfidence),
    skinReference: reference,
    notes,
  };
};

export const heuristicHairOcclusionAnalyzer: HairOcclusionAnalyzer = {
  mode: 'heuristic',
  analyse: analyseHeuristically,
};
